import { S25Util } from "../../../../../util/s25-util";
import { DashPanelListItemBean } from "./home.dash.panel.list.item.types";

type CountBucket = "none" | "single" | "multiple";

type LocalizedBucket = Partial<Record<CountBucket, string>>;
type NotextBucket = Partial<Record<CountBucket, boolean>>;

/** Subset of legacy `S25DitemLangService` used to format dash list row labels. */
export type DashDitemDisplayLang = {
    main: {
        core: {
            name: {
                multiple: Partial<Record<number, string>>;
                notext: Partial<Record<number, NotextBucket>>;
                prefix: Partial<Record<number, LocalizedBucket>>;
                label: Partial<Record<number, LocalizedBucket>>;
            };
            label: Partial<Record<number, string>>;
            labelItalic: Partial<Record<number, string>>;
        };
    };
};

function countBucket(itemName: DashPanelListItemBean["itemName"]): CountBucket {
    if (itemName == 0) {
        return "none";
    }
    if (itemName == 1) {
        return "single";
    }
    return "multiple";
}

function isMultipleItem(item: DashPanelListItemBean): boolean {
    const nestedItems = item.item;
    return S25Util.array.isArray(nestedItems) && nestedItems.length > 1;
}

function bucketText(bucketMap: LocalizedBucket | undefined, bucket: CountBucket): string | undefined {
    return bucketMap?.[bucket];
}

function bucketOmitsCount(bucketMap: NotextBucket | undefined, bucket: CountBucket): boolean {
    return !!bucketMap?.[bucket];
}

/** Build ditem display lang from app lang — mirrors `_S25DitemLangType.getLang`. */
export function buildDashDitemDisplayLang(lang: {
    div: { controls: Record<string, { text?: Record<string, unknown> }> };
}): DashDitemDisplayLang {
    const langLayout = lang.div.controls.layout?.text ?? {};
    const langMy25live = lang.div.controls["s25-my25live"]?.text ?? {};

    const my25Text = langMy25live as Record<string, string>;
    const layoutText = langLayout as Record<string, string>;

    return {
        main: {
            core: {
                name: {
                    multiple: {
                        4: layoutText.locations,
                        1104: layoutText.locations,
                        1204: layoutText.locations,
                        6: layoutText.resources,
                        1106: layoutText.resources,
                        1206: layoutText.resources,
                    },
                    notext: {
                        10051: { none: true },
                        10052: { none: true },
                        10101: { none: true, single: true },
                    },
                    prefix: {
                        10101: {
                            multiple: my25Text.you_have,
                        },
                    },
                    label: {
                        10001: { single: my25Text.event, multiple: my25Text.events },
                        10002: { single: my25Text.event, multiple: my25Text.events },
                        10003: { single: my25Text.event, multiple: my25Text.events },
                        10004: { single: my25Text.event, multiple: my25Text.events },
                        10051: {
                            none: my25Text.no_event_drafts,
                            single: my25Text.draft,
                            multiple: my25Text.drafts,
                        },
                        10052: {
                            none: my25Text.no_event_drafts,
                            single: my25Text.draft,
                            multiple: my25Text.drafts,
                        },
                        10101: {
                            none: my25Text.no_tasks,
                            single: my25Text.one_task,
                            multiple: my25Text.tasks_on_agenda,
                        },
                        10102: {
                            none: my25Text.outstanding_tasks,
                            single: my25Text.outstanding_task,
                            multiple: my25Text.outstanding_tasks,
                        },
                        10103: {
                            none: my25Text.flagged_tasks,
                            single: my25Text.flagged_task,
                            multiple: my25Text.flagged_tasks,
                        },
                        10104: {
                            none: my25Text.tasks_you_assigned,
                            single: my25Text.task_you_assigned,
                            multiple: my25Text.tasks_you_assigned,
                        },
                    },
                },
                label: {
                    10001: my25Text.in_which,
                    10002: my25Text.in_which,
                    10003: my25Text.in_which,
                    10004: my25Text.in_which_additional_role,
                    10051: my25Text.in_which,
                    10052: my25Text.in_which,
                },
                labelItalic: {
                    10001: my25Text.requestor,
                    10002: my25Text.scheduler,
                    10003: my25Text.instructor,
                    10051: my25Text.scheduler,
                    10052: my25Text.requestor,
                },
            },
        },
    };
}

/** Legacy `S25DitemServiceBase.main.core.name.text`. */
export function formatDashListItemCoreName(item: DashPanelListItemBean, ditemLang: DashDitemDisplayLang): string {
    const itemTypeId = item.itemTypeId;
    if (itemTypeId === undefined) {
        return String(item.itemName ?? "");
    }

    if (isMultipleItem(item)) {
        const nestedItems = item.item as unknown[];
        const multipleLabel = ditemLang.main.core.name.multiple[itemTypeId];
        return `${nestedItems.length}${multipleLabel ? ` ${multipleLabel}` : ""}`;
    }

    if (item.isInlineQuantity) {
        const quantitySuffix = item.quantity !== undefined && item.quantity !== null ? ` [${item.quantity}]` : "";
        return `${item.itemName ?? ""}${quantitySuffix}`;
    }

    const bucket = countBucket(item.itemName);
    const nameLang = ditemLang.main.core.name;
    const prefix = bucketText(nameLang.prefix[itemTypeId], bucket);
    const label = bucketText(nameLang.label[itemTypeId], bucket);
    const omitCount = bucketOmitsCount(nameLang.notext[itemTypeId], bucket);

    const countPart = omitCount ? "" : `${prefix ? " " : ""}${item.itemName ?? ""}`;
    const labelPart = label ? `${!omitCount || prefix ? " " : ""}${label}` : "";

    return `${prefix ?? ""}${countPart}${labelPart}`.trim();
}

/** Legacy `S25DitemServiceBase.main.core.label` (excludes pre-label rows). */
export function formatDashListItemCoreLabel(
    item: DashPanelListItemBean,
    ditemLang: DashDitemDisplayLang,
): string | undefined {
    if (item.isPrelabel) {
        return undefined;
    }
    const itemTypeId = item.itemTypeId;
    if (item.itemLabel) {
        return item.itemLabel;
    }
    if (itemTypeId === undefined) {
        return undefined;
    }
    return ditemLang.main.core.label[itemTypeId];
}

/** Legacy `S25DitemServiceBase.main.core.labelItalic`. */
export function formatDashListItemCoreLabelItalic(
    item: DashPanelListItemBean,
    ditemLang: DashDitemDisplayLang,
): string | undefined {
    const itemTypeId = item.itemTypeId;
    if (itemTypeId === undefined) {
        return undefined;
    }
    return ditemLang.main.core.labelItalic[itemTypeId];
}

export function formatDashListItemCoreDesc(item: DashPanelListItemBean): string | undefined {
    const itemDesc = item.itemDesc;
    return itemDesc && itemDesc !== "" ? itemDesc : undefined;
}

export function formatDashListItemAriaLabel(item: DashPanelListItemBean, ditemLang: DashDitemDisplayLang): string {
    return [
        formatDashListItemCoreName(item, ditemLang),
        formatDashListItemCoreLabel(item, ditemLang),
        formatDashListItemCoreLabelItalic(item, ditemLang),
    ]
        .filter(Boolean)
        .join(" ");
}
